import type {
  OnboardingInterest,
  OnboardingSelfDescription,
} from "@repo/types";
import { ArrowRight01Icon } from "~/components/icons/central-icons";
import { Button } from "~/components/ui/button";
import { GlassOptionButton } from "~/components/ui/glass-option-button";
import { Input } from "~/components/ui/input";
import { m as messages } from "~/lib/i18n";

export type UsernameCheckState =
  | "idle"
  | "checking"
  | "error"
  | "available"
  | "invalid"
  | "reserved"
  | "saving"
  | "unavailable";

const SELF_DESCRIPTION_OPTIONS: readonly OnboardingSelfDescription[] = [
  "long_term_investor",
  "active_trader",
  "new_to_investing",
  "hands_off",
  "diversified",
  "concentrated",
];

const INTEREST_OPTION_ROWS = [
  ["blue_chip_stocks", "consumer_brands", "bonds", "crypto", "commodities"],
  [
    "steady_income",
    "currencies",
    "memecoins",
    "pre_ipo",
    "notable_investor_trackers",
  ],
] as const satisfies readonly (readonly OnboardingInterest[])[];

function getSelfDescriptionLabel(value: OnboardingSelfDescription): string {
  switch (value) {
    case "long_term_investor":
      return messages.onboarding_v1_profile_long_term();
    case "active_trader":
      return messages.onboarding_v1_profile_active_trader();
    case "new_to_investing":
      return messages.onboarding_v1_profile_new_investor();
    case "hands_off":
      return messages.onboarding_v1_profile_hands_off();
    case "diversified":
      return messages.onboarding_v1_profile_diversified();
    case "concentrated":
      return messages.onboarding_v1_profile_concentrated();
  }
}

function getInterestLabel(value: OnboardingInterest): string {
  switch (value) {
    case "blue_chip_stocks":
      return messages.onboarding_v1_interest_blue_chip_stocks();
    case "consumer_brands":
      return messages.onboarding_v1_interest_consumer_brands();
    case "bonds":
      return messages.onboarding_v1_interest_bonds();
    case "crypto":
      return messages.onboarding_v1_interest_crypto();
    case "commodities":
      return messages.onboarding_v1_interest_commodities();
    case "steady_income":
      return messages.onboarding_v1_interest_steady_income();
    case "currencies":
      return messages.onboarding_v1_interest_currencies();
    case "memecoins":
      return messages.onboarding_v1_interest_memecoins();
    case "pre_ipo":
      return messages.onboarding_v1_interest_pre_ipo();
    case "notable_investor_trackers":
      return messages.onboarding_v1_interest_notable_investor_trackers();
  }
}

function UsernameStatus(props: { readonly state: UsernameCheckState }) {
  switch (props.state) {
    case "idle":
    case "checking":
    case "saving":
      return null;
    case "error":
      return (
        <p className="onboarding-v1__username-status" role="alert">
          {messages.onboarding_v1_username_check_retry()}
        </p>
      );
    case "available":
      return (
        <p
          className="onboarding-v1__username-status onboarding-v1__username-status--available"
          role="status"
        >
          {messages.onboarding_v1_username_available()}
        </p>
      );
    case "invalid":
      return (
        <p className="onboarding-v1__username-status" role="alert">
          {messages.onboarding_v1_username_invalid()}
        </p>
      );
    case "reserved":
      return (
        <p className="onboarding-v1__username-status" role="alert">
          {messages.onboarding_v1_username_reserved()}
        </p>
      );
    case "unavailable":
      return (
        <p className="onboarding-v1__username-status" role="alert">
          {messages.onboarding_v1_username_unavailable()}
        </p>
      );
  }
}

export function OnboardingV1NameStep(props: {
  readonly checkState: UsernameCheckState;
  readonly onChange: (value: string) => void;
  readonly onContinue: () => void;
  readonly onReturningUserLogin: () => void;
  readonly showReturningUserLogin: boolean;
  readonly username: string;
}) {
  const statusId = "onboarding-v1-username-status";

  return (
    <div className="onboarding-v1__question-step onboarding-v1__name-step">
      <form
        className="onboarding-v1__name-form"
        onSubmit={(event) => {
          event.preventDefault();
          props.onContinue();
        }}
      >
        <div className="onboarding-v1__question-heading">
          <p>{messages.onboarding_v1_username_eyebrow()}</p>
          <h1>{messages.onboarding_v1_username_heading()}</h1>
        </div>
        <div className="onboarding-v1__username-control">
          <label className="sr-only" htmlFor="onboarding-v1-username">
            {messages.onboarding_v1_username_label()}
          </label>
          <Input
            aria-describedby={statusId}
            aria-invalid={
              props.checkState === "invalid" ||
              props.checkState === "reserved" ||
              props.checkState === "unavailable"
            }
            autoCapitalize="none"
            autoComplete="username"
            className="onboarding-v1__username-input"
            id="onboarding-v1-username"
            maxLength={20}
            onChange={(event) => props.onChange(event.currentTarget.value)}
            placeholder={messages.onboarding_v1_username_placeholder()}
            spellCheck={false}
            value={props.username}
          />
          <Button
            aria-label={messages.onboarding_v1_username_continue_action()}
            className="onboarding-v1__username-submit"
            disabled={
              props.checkState !== "available" && props.checkState !== "error"
            }
            loading={
              props.checkState === "checking" || props.checkState === "saving"
            }
            size="icon"
            type="submit"
          >
            <ArrowRight01Icon className="size-5" />
          </Button>
        </div>
        <div className="onboarding-v1__username-status-slot" id={statusId}>
          <UsernameStatus state={props.checkState} />
        </div>
        {props.showReturningUserLogin ? (
          <p className="onboarding-v1__login-prompt">
            {messages.onboarding_v1_username_existing_account()}{" "}
            <button onClick={props.onReturningUserLogin} type="button">
              {messages.onboarding_v1_username_login_action()}
            </button>
          </p>
        ) : null}
      </form>
    </div>
  );
}

function OptionButton(props: {
  readonly optionId?: OnboardingInterest | "unknown";
  readonly label: string;
  readonly onToggle: () => void;
  readonly selected: boolean;
}) {
  return (
    <GlassOptionButton
      data-onboarding-option={props.optionId}
      onClick={props.onToggle}
      selected={props.selected}
    >
      {props.label}
    </GlassOptionButton>
  );
}

export function OnboardingV1ProfileStep(props: {
  readonly onContinue: () => void;
  readonly onToggle: (value: OnboardingSelfDescription) => void;
  readonly selected: readonly OnboardingSelfDescription[];
}) {
  return (
    <div className="onboarding-v1__question-step onboarding-v1__flow-step">
      <div className="onboarding-v1__question-content onboarding-v1__flow-body onboarding-v1__profile-content">
        <h1>{messages.onboarding_v1_profile_heading()}</h1>
        <div
          aria-label={messages.onboarding_v1_profile_group_label()}
          className="onboarding-v1__profile-options"
          role="group"
        >
          {SELF_DESCRIPTION_OPTIONS.map((option) => (
            <OptionButton
              key={option}
              label={getSelfDescriptionLabel(option)}
              onToggle={() => props.onToggle(option)}
              selected={props.selected.includes(option)}
            />
          ))}
        </div>
      </div>
      <div className="onboarding-v1__flow-actions">
        <Button
          className="onboarding-v1__continue"
          disabled={props.selected.length === 0}
          onClick={props.onContinue}
          size="lg"
          type="button"
          variant="inverse"
        >
          {messages.flagship_onboarding_continue_label()}
        </Button>
      </div>
    </div>
  );
}

export function OnboardingV1InterestsStep(props: {
  readonly interestsUnknown: boolean;
  readonly onContinue: () => void;
  readonly onToggle: (value: OnboardingInterest) => void;
  readonly onToggleUnknown: () => void;
  readonly selected: readonly OnboardingInterest[];
  readonly username: string;
}) {
  const hasAnswer = props.interestsUnknown || props.selected.length > 0;
  const heading = props.username
    ? messages.onboarding_v1_interests_heading({ username: props.username })
    : messages.onboarding_v1_interests_heading_without_username();

  return (
    <div className="onboarding-v1__question-step onboarding-v1__flow-step">
      <div className="onboarding-v1__question-content onboarding-v1__flow-body onboarding-v1__interests-content">
        <h1>{heading}</h1>
        <div
          aria-label={messages.onboarding_v1_interests_group_label()}
          className="onboarding-v1__interest-options"
          role="group"
        >
          {INTEREST_OPTION_ROWS.map((options) => (
            <div
              className="onboarding-v1__interest-option-row"
              key={options[0]}
            >
              {options.map((option) => (
                <OptionButton
                  key={option}
                  label={getInterestLabel(option)}
                  onToggle={() => props.onToggle(option)}
                  optionId={option}
                  selected={props.selected.includes(option)}
                />
              ))}
            </div>
          ))}
          <div className="onboarding-v1__interest-unknown-row">
            <OptionButton
              label={messages.onboarding_v1_interest_unknown()}
              onToggle={props.onToggleUnknown}
              optionId="unknown"
              selected={props.interestsUnknown}
            />
          </div>
        </div>
      </div>
      <div className="onboarding-v1__flow-actions">
        <Button
          className="onboarding-v1__continue"
          disabled={!hasAnswer}
          onClick={props.onContinue}
          size="lg"
          type="button"
          variant="inverse"
        >
          {messages.flagship_onboarding_continue_label()}
        </Button>
      </div>
    </div>
  );
}
